import { List, Map, Record } from 'immutable'
import { FLUSH, REHYDRATE } from 'redux-persist'
import { combineActions, handleActions } from 'redux-actions'

import { ProTrack, PublicProTrack } from 'store/records/proTracks'
import { PER_PAGE } from 'app/constants/pagination'
import { getIdentityProgress } from 'store/entities/identity/actions'
import * as actions from './actions'
import * as levelsActions from './levels/actions'
import levelsReducer, { Levels } from './levels/reducer'
import * as activitiesActions from './activities/actions'
import activitiesReducer, { Activities } from './activities/reducer'
import * as ratesActions from './rates/actions'
import unsavedChangesReducer, { UnsavedChanges } from './unsavedChanges/reducer'
import * as unsavedChangesActions from './unsavedChanges/actions'
import ratesReducer, { Rates } from './rates/reducer'
import * as generatorActions from '../generator/actions'
import * as galleryActions from '../gallery/actions'

export const Loaded = Record({
  all: false,
  current: false,
  public: false,
  published: false,
})

export const Protracks = Record({
  all: Map(),
  public: Map({
    selected: '',
    protracks: Map(),
  }),
  published: Map(),
  publishedLibraries: Map(),
  view: null,
  pagination: Map({
    current: 0,
    perPage: PER_PAGE,
  }),
  sort: Map({
    by: null,
    desc: false,
  }),
  filter: '',
  filterLibraries: '',
  loaded: new Loaded(),
  levels: new Levels(),
  activities: new Activities(),
  rates: new Rates(),
  unsavedChanges: new UnsavedChanges(),
  pending: false,
})

const handleRehydrate = (state, { payload }) => {
  return payload && payload.protracks && payload.protracks.set
    ? payload.protracks
        .set('loaded', new Loaded())
        .setIn(['levels', 'loaded'], false)
        .setIn(['activities', 'loaded'], false)
        .setIn(['rates', 'loaded'], false)
        .set('unsavedChanges', new UnsavedChanges())
    : state
}

const handleFlush = () => {
  return new Protracks()
}

const handleGetAllProtracksSuccess = (state, { payload: { data } }) => {
  return state.set(
    'all',
    data.reduce((protracks, protrack) => {
      return protracks.set(protrack.id.toString(), new ProTrack(protrack))
    }, Map())
  )
}

const handleGetPublishedProtracksRequest = state => state.set('pending', true)

const handleGetPublishedProtracksSuccess = (state, { payload: { data } }) => {
  return state
    .set(
      'published',
      data.reduce((protracks, protrack) => {
        const levels = List(protrack.levels.map(level => Map(level)))
        return protracks.set(protrack.id.toString(), new ProTrack({ ...protrack, levels }))
      }, Map())
    )
    .set('pending', false)
}

const handleFetchPublishedProTracksLibrariesSuccess = (state, { payload: { data } }) => {
  return state
    .set(
      'publishedLibraries',
      data.reduce((proTracks, proTrack) => {
        const libraryLevels = List(proTrack.libraryLevels.map(level => Map(level)))
        return proTracks.set(proTrack.id.toString(), new ProTrack({ ...proTrack, libraryLevels }))
      }, Map())
    )
    .set('pending', false)
}

const handleFetchPublicProTracksSuccess = (state, { payload: { data } }) => {
  const protracks = data.reduce(
    (protracks, protrack) =>
      protracks.set(
        protrack.id.toString(),
        new PublicProTrack({
          ...protrack,
          organizationName: (protrack.organization && protrack.organization.name) || 'global',
        })
      ),
    Map()
  )
  return state.setIn(['public', 'protracks'], protracks)
}

const handleSelectPublicProtrack = (state, { payload: { id } }) => {
  return state.setIn(['public', 'selected'], parseInt(id)).setIn(['pagination', 'current'], 0)
}

const handleGetByIdRequest = state => {
  return state.setIn(['loaded', 'current'], false)
}

const handleGetByIdSuccess = (state, { payload: { data } }) => {
  return state
    .setIn(['loaded', 'current'], true)
    .setIn(['all', data.id.toString()], new ProTrack(data))
    .set('view', data.id.toString())
}

const handlePostSuccess = (state, { payload: { data } }) => {
  return state
    .setIn(['loaded', 'current'], true)
    .setIn(['all', data.id.toString()], new ProTrack(data))
    .set('view', data.id.toString())
}

const handlePutSuccess = (state, { payload: { data } }) => {
  return state.setIn(['all', data.id.toString()], new ProTrack(data))
}

const handleSetView = (state, { payload }) => {
  return (
    state
      .set('view', payload.id.toString())
      // each time we change proTrack, we should set activity.loaded to false due to load activities for new proTrack
      .setIn(['activities', 'loaded'], false)
  )
}

const handleSelectPage = (state, { payload }) => {
  return state.setIn(['pagination', 'current'], payload.page)
}

const handleSortBy = (state, { payload }) => {
  return state.setIn(['sort', 'by'], payload.by).setIn(['sort', 'desc'], payload.desc)
}

const handleFilterBy = (state, { payload }) => {
  return state.set('filter', payload.by)
}

const handleFilterLibrariesBy = (state, { payload }) => {
  return state.set('filterLibraries', payload.by)
}

const handleChangeLevelView = (state, { payload }) => {
  return (
    state
      .setIn(['levels', 'view'], payload.id.toString())
      // each time we change proTrack, we should set activity.loaded to false due to load activities for new level
      .setIn(['activities', 'loaded'], false)
  )
}

const handleSavePublicProTrack = (state, { payload: { data } }) => {
  return state.setIn(['public', 'protracks', data.id.toString()], new PublicProTrack(data)).set('pending', false)
}

const handleDeletePublicProTrack = (state, { meta: { proTrackId } }) => {
  return state.deleteIn(['public', 'protracks', proTrackId.toString()]).set('pending', false)
}

const handleSetPending = state => {
  return state.set('pending', true)
}

const handleSetUnPending = state => {
  return state.set('pending', false)
}

const handleCloneProTrackSuccess = (state, { payload: { data } }) => {
  return state.setIn(['all', data.id.toString()], new ProTrack(data))
}

const handlePutProTrackSuccess = (state, { payload: { data } }) => {
  return state.setIn(['all', data.id.toString()], new ProTrack(data))
}

const handleRemoveProTrack = (state, { payload: { data: removedProTrackId } }) => {
  const updatedProTracks = state.get('all').delete(`${removedProTrackId}`)
  return state.set('all', updatedProTracks)
}

export default handleActions(
  {
    [REHYDRATE]: handleRehydrate,
    [FLUSH]: handleFlush,
    [actions.getProtrackById]: handleGetByIdRequest,
    [actions.getProtrackById.success]: handleGetByIdSuccess,
    [actions.postProtrack.success]: handlePostSuccess,
    [actions.putProtrack.success]: handlePutSuccess,
    [actions.getAllProtracks.success]: handleGetAllProtracksSuccess,
    [actions.getPublishedProtracks.success]: handleGetPublishedProtracksSuccess,
    [actions.fetchPublishedProTracksLibraries.success]: handleFetchPublishedProTracksLibrariesSuccess,
    [combineActions(actions.getPublishedProtracks, actions.fetchPublishedProTracksLibraries)]:
      handleGetPublishedProtracksRequest,
    [actions.fetchPublicProTracks.success]: handleFetchPublicProTracksSuccess,
    [actions.selectPublicProtrack]: handleSelectPublicProtrack,
    [actions.setView]: handleSetView,
    [actions.selectPage]: handleSelectPage,
    [actions.sortBy]: handleSortBy,
    [actions.filterBy]: handleFilterBy,
    [actions.filterLibrariesBy]: handleFilterLibrariesBy,
    [actions.putProtrack.success]: handlePutProTrackSuccess,
    [combineActions(actions.createPublicProTrack, actions.deletePublicProTrack)]: handleSetPending,
    [combineActions(actions.createPublicProTrack.success)]: handleSavePublicProTrack,
    [actions.deletePublicProTrack.success]: handleDeletePublicProTrack,
    [combineActions(actions.createPublicProTrack.fail, actions.deletePublicProTrack.fail)]: handleSetUnPending,
    [levelsActions.setView]: handleChangeLevelView,
    // Levels reducer
    [combineActions(
      levelsActions.getLevels,
      levelsActions.getLevels.success,
      levelsActions.createLevel.success,
      levelsActions.updateLevel.success,
      levelsActions.setOrder,
      levelsActions.setDeleted,
      levelsActions.setBeforeCreateOrder,
      levelsActions.publishLevel.success,
      getIdentityProgress.success
    )]: (state, action) => {
      return state.set('levels', levelsReducer(state.get('levels'), action))
    },
    // Activities reducer
    [combineActions(
      activitiesActions.getActivities,
      activitiesActions.getActivities.success,
      activitiesActions.postActivity.success,
      activitiesActions.putActivity.success,
      activitiesActions.setView,
      activitiesActions.setOrder,
      activitiesActions.setBeforeCreateOrder,
      activitiesActions.setDeleted,
      activitiesActions.setPinned,
      activitiesActions.addActivitiesFromFile,
      activitiesActions.replaceOldImageLinkInActivities,
      activitiesActions.deleteTempActivity,
      activitiesActions.addActivityToExamples,
      activitiesActions.addActivityToExamples.success,
      activitiesActions.addActivityToExamples.fail,
      activitiesActions.removeActivityFromExamples,
      activitiesActions.removeActivityFromExamples.success,
      activitiesActions.removeActivityFromExamples.fail,
      activitiesActions.updateActivityImage,
      activitiesActions.fetchCategoriesTagsForProTrackLevel.success,
      generatorActions.replaceActivity,
      activitiesActions.postActivitiesListBulk.success,
      galleryActions.deleteImageFromGallery.success,
      activitiesActions.triggerProtrackLevelActivitiesValidation
    )]: (state, action) => {
      return state.set('activities', activitiesReducer(state.get('activities'), action))
    },
    // Rates and comments reducer
    [combineActions(ratesActions.getRates.success, ratesActions.getRates.fail, ratesActions.selectPage)]: (
      state,
      action
    ) => {
      return state.set('rates', ratesReducer(state.get('rates'), action))
    },
    // Unsaved changes reducer
    [combineActions(
      unsavedChangesActions.setBlockedPathname,
      unsavedChangesActions.reset,
      levelsActions.setOrder,
      levelsActions.setDeleted,
      activitiesActions.setOrder,
      activitiesActions.setDeleted,
      activitiesActions.setPinned,
      activitiesActions.addActivitiesFromFile,
      generatorActions.replaceActivity
    )]: (state, action) => {
      return state.set('unsavedChanges', unsavedChangesReducer(state.get('unsavedChanges'), action))
    },
    [actions.cloneProTrack.success]: handleCloneProTrackSuccess,
    [actions.deleteProTrack.success]: handleRemoveProTrack,
  },
  new Protracks()
)
